import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToSection } from "@/components/Reveal";

const LINKS = [
    { label: "Pillars", href: "#pillars" },
    { label: "How it works", href: "#how" },
    { label: "Network", href: "#network" },
];

const go = (e, href) => {
    e.preventDefault();
    scrollToSection(href);
};

export default function Navbar() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const close = () => setOpen(false);
        window.addEventListener("resize", close);
        return () => window.removeEventListener("resize", close);
    }, []);

    return (
        <header className="fixed top-0 left-0 right-0 z-50 glass-strong border-b border-white/10">
            <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <a href="#" data-testid="nav-logo-link" onClick={(e) => go(e, "#top")} className="shrink-0">
                    <Logo id="nav" />
                </a>

                <div className="hidden items-center gap-8 md:flex">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={(e) => go(e, l.href)}
                            className="font-mono text-xs uppercase tracking-[0.18em] text-slate-400 transition-colors hover:text-neon-cyan"
                        >
                            {l.label}
                        </a>
                    ))}
                    <span className="hidden items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 lg:flex">
                        <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-neon-emerald" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                            Network operational
                        </span>
                    </span>
                </div>

                <div className="flex items-center gap-3">
                    <a
                        href="#waitlist"
                        data-testid="nav-cta-button"
                        onClick={(e) => go(e, "#waitlist")}
                        className="group hidden items-center gap-1.5 rounded-full bg-neon-cyan px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.14em] text-void transition-all duration-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.45)] sm:inline-flex"
                    >
                        Join waitlist
                        <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                    <button
                        data-testid="nav-mobile-button"
                        onClick={() => setOpen((v) => !v)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-slate-300 md:hidden"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden border-t border-white/10 bg-void/95 md:hidden"
                    >
                        <div className="space-y-1 px-4 py-4">
                            {LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={(e) => go(e, l.href)}
                                    className="block rounded-lg px-3 py-3 font-mono text-xs uppercase tracking-[0.18em] text-slate-300 hover:bg-white/5 hover:text-neon-cyan"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#waitlist"
                                onClick={(e) => go(e, "#waitlist")}
                                className="mt-2 block rounded-lg bg-neon-cyan px-3 py-3 text-center font-mono text-xs font-bold uppercase tracking-[0.14em] text-void"
                            >
                                Join waitlist
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}